// Need to use the React-specific entry point to import createApi
import _ from "lodash";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    startSession,
    startCourseAttempt,
    startDeckAttempt,
} from "../../redux/attemptSlice";
import { organizeArticles } from "../../redux/articlesSlice";
import { settings } from "../settings/app.settings";
import { updateUser } from "../../redux/authSlice";

export const baseUrl = settings.baseUrl;
export const clientAssetsPath = settings.clientAssetsPath;
export const targetRoute = settings.targetRoute;

function getChanged(array, key) {
    return _.uniq(
        _.map(
            _.filter(array, (element) => _.has(element, `payload.${key}`)),
            (element) => _.get(element, `payload.${key}`)
        )
    );
}

export const apiV6 = createApi({
    reducerPath: "apiV6",
    baseQuery: fetchBaseQuery({
        baseUrl: `${baseUrl}api/v3`,
        prepareHeaders: (headers, { getState, endpoint }) => {
            headers.set("Access-Control-Allow-Origin", "*");
            headers.set("access-token", getState().auth.token);
            return headers;
        },
    }),
    tagTypes: [
        "TrackerDep",
        "EngageDep",
        "NotifyDep",
        "Deck",
        "Leaderboard",
        "TicketInventory",
        "ArticleInventory",
        "CourseInventory",
        "CourseLearners",
        "ContestInventory",
        "ContestLearners",
        "EditingDeck",
        "SocialInventory",
    ],
    endpoints: (builder) => ({
        // #region Core Routes
        // ==========================================
        getBanners: builder.query({
            query: () => `/banners/published_list`,
        }),
        getSessions: builder.query({
            query: (id) => `/activity/${id}/sessions`,
            providesTags: ["TrackerDep"],
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(startSession(data));
                } catch (err) {
                    console.log("There was a session fetching error");
                }
            },
        }),
        //#endregion

        // #region Course Routes
        // ==========================================
        getCourseLibrary: builder.query({
            query: () => `/courseLibrary`,
        }),
        getProgram: builder.query({
            query: (id) => `/programs/${id}/show`,
            providesTags: ["TrackerDep"],
        }),
        getCourse: builder.query({
            query: (prm) => `/courses/${prm.id}/learners/${prm.user_id}/show`,
            providesTags: ["TrackerDep"],
            async onQueryStarted(prm, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        startCourseAttempt({
                            id: prm.id,
                            currentState: data.currentState,
                            history: data.history,
                        })
                    );
                } catch (err) {
                    console.log("There was a course fetching error");
                }
            },
        }),
        getDeck: builder.query({
            query: (prm) =>
                `/decks/${prm.id}/learners/${prm.user_id}/show/${
                    prm.readerType || ""
                }`,
            providesTags: (result, error, arg) => {
                return [{ type: "Deck", id: arg.id }, "Deck"];
            },
            async onQueryStarted(prm, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        startDeckAttempt({
                            id: prm.id,
                            currentState: data.currentState,
                            readerType: data.readerType,
                        })
                    );
                } catch (err) {
                    console.log("There was a deck fetching error");
                }
            },
        }),
        //#endregion

        // #region Search Routes
        // ==========================================
        sendSearchHistory: builder.mutation({
            query: (term) => ({
                url: `/searches/save`,
                method: "POST",
                body: { term: term },
            }),
        }),
        //#endregion

        // #region Notification Routes
        // ==========================================
        getNotifications: builder.query({
            query: (id) => `/users/${id}/notifications/fetch/all`,
            providesTags: ["NotifyDep"],
        }),
        getUnreadCount: builder.query({
            query: (id) => `/users/${id}/notifications/unread`,
            providesTags: ["NotifyDep"],
        }),
        sendNotificationsRead: builder.mutation({
            query: (id) => ({
                url: `/users/${id}/notifications/update/markread`,
                method: "POST",
            }),
            invalidatesTags: ["NotifyDep"],
        }),
        // #endregion

        // #region Article Routes
        // ==========================================
        getArticles: builder.query({
            query: () => `/articles/articleLibrary`,
            async onQueryStarted(prm, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        organizeArticles({
                            list: data.blog,
                        })
                    );
                } catch (err) {
                    console.log("There was an articles fetching error");
                }
            },
        }),
        getArticle: builder.query({
            query: (prm) =>
                `/articles/${prm.id}/learners/${prm.user_id}/show/${
                    prm.readerType || ""
                }`,
            providesTags: ["EngageDep"],
        }),
        getUnauthArticle: builder.query({
            query: (identifier) => `/articles/${identifier}/unauth`,
            providesTags: ["EngageDep"],
        }),
        getRecentViews: builder.query({
            query: () => `/articles/recent`,
            providesTags: ["TrackerDep"],
        }),
        sendArticlesComments: builder.mutation({
            query: (prm) => ({
                url: `/articles/${prm.id}/comments/create`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["EngageDep"],
        }),
        sendArticlesLikes: builder.mutation({
            query: (prm) => ({
                url: `/articles/${prm.id}/like`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["EngageDep"],
        }),
        sendArticlesDislikes: builder.mutation({
            query: (prm) => ({
                url: `/articles/${prm.id}/dislike`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["EngageDep"],
        }),
        getTags: builder.query({
            query: () => `/tags/list`,
        }),
        // #endregion

        // #region Data Tracker Routes
        // ==========================================
        sendTracker: builder.mutation({
            query: (tracker) => ({
                url: "/activity/submit_queue",
                method: "POST",
                body: tracker,
            }),
            invalidatesTags: (result, error, arg) => {
                let invalidatables = [{ type: "TrackerDep" }];
                const deckValues = getChanged(result, "deck");
                _.each(deckValues, (deck) => {
                    invalidatables.push({ type: "Deck", id: deck });
                });
                return invalidatables;
            },
        }),
        // #endregion

        // #region Leaderboard Routes
        // ==========================================
        getAppLeaderboard: builder.query({
            query: () => `/leaderboards/appleaderboard`,
            providesTags: ["TrackerDep", "Leaderboard"],
        }),
        getCourseLeaderboard: builder.query({
            query: (id) => `/courses/${id}/leaderboards/show`,
            providesTags: ["TrackerDep", "Leaderboard"],
        }),
        getDeckLeaderboard: builder.query({
            query: (id) => `/decks/${id}/leaderboards/allParameters`,
            providesTags: ["TrackerDep", "Leaderboard"],
        }),
        getCollectibles: builder.query({
            query: (id) => `/users/${id}/myprofile`,
            providesTags: ["TrackerDep", "Leaderboard"],
        }),
        // #endregion

        // #region Analytics Routes
        // ==========================================
        getLearnerProgress: builder.query({
            query: (id) => `/analytics/${id}/progress`,
            providesTags: ["TrackerDep"],
        }),
        getLearnerCourseProgress: builder.query({
            query: (prm) => `/analytics/${prm.userId}/${prm.courseId}/progress`,
            providesTags: ["TrackerDep"],
        }),
        getLearnerPerformance: builder.query({
            query: (id) => `/analytics/${id}/performance`,
            providesTags: ["TrackerDep"],
        }),
        getLearnerCoursePerformance: builder.query({
            query: (prm) =>
                `/analytics/${prm.userId}/${prm.courseId}/performance`,
            providesTags: ["TrackerDep"],
        }),
        getActivityLog: builder.query({
            query: (id) => `/activity/${id}/log`,
            providesTags: ["TrackerDep"],
        }),
        getBuilderCourseLeaderboard: builder.query({
            query: (id) => `/courses/${id}/leaderboards/finalexam`,
            providesTags: ["TrackerDep"],
        }),
        getDeckLearnerPath: builder.query({
            query: (prm) =>
                `/analytics/${prm.userId}/${prm.courseId}/${prm.deckId}/learners/${prm.scope}`,
            providesTags: ["TrackerDep"],
        }),
        getCourseLearnersPath: builder.query({
            query: (prm) =>
                `/analytics/${prm.userId}/${prm.courseId}/learners/${prm.scope}`,
        }),
        getDeckPath: builder.query({
            query: (prm) => `decks/${prm.deckId}/show`,
            providesTags: ["TrackerDep"],
        }),
        getSurveyStatusPath: builder.query({
            query: (prm) =>
                `/analytics/questions/${prm.deckId}/responses/learners`,
            providesTags: ["TrackerDep"],
        }),
        getSurveyQuestionsPath: builder.query({
            query: (prm) =>
                `/analytics/questions/${prm.deckId}/responses/questions`,
            providesTags: ["TrackerDep"],
        }),

        // #endregion

        // #region Edit Profile Routes
        // ==========================================
        sendUserSelfUpdate: builder.mutation({
            query: (prm) => ({
                url: `/users/${prm.id}/update/self`,
                method: "POST",
                body: prm,
            }),
            async onQueryStarted(prm, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        updateUser({
                            user: {
                                first_name: data.first_name,
                                last_name: data.last_name,
                                lang: data.lang,
                                avatarConfig: data.avatarConfig,
                                image: data.image,
                            },
                        })
                    );
                } catch (err) {
                    console.log("There was an articles fetching error");
                }
            },
            invalidatesTags: ["Leaderboard"],
        }),
        // #endregion

        // #region Social Routes
        // ==========================================
        getPosts: builder.query({
            query: () => `/social/feed`,
            providesTags: ["SocialInventory"],
        }),
        sendSocialPost: builder.mutation({
            query: (post) => ({
                url: `/social/posts/create`,
                method: "POST",
                body: post,
            }),
            invalidatesTags: ["SocialInventory"],
        }),
        sendSocialComments: builder.mutation({
            query: (prm) => ({
                url: `/social/posts/${prm.id}/comments/create`,
                method: "POST",
                body: prm,
            }),
        }),
        sendSocialLikes: builder.mutation({
            query: (prm) => ({
                url: `/social/posts/${prm.id}/like`,
                method: "POST",
                body: prm,
            }),
        }),
        sendSocialDelete: builder.mutation({
            query: (id) => ({
                url: `/social/posts/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["SocialInventory"],
        }),

        // #endregion

        // #region Ticket Routes
        // ==========================================
        getTickets: builder.query({
            query: () => `/ticket/list`,
            providesTags: ["TicketInventory"],
        }),
        submitTicket: builder.mutation({
            query: (prm) => ({
                url: `/ticket/create`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["TicketInventory"],
        }),

        // #endregion

        // #region Creator Routes
        // ==========================================
        getCourseInventory: builder.query({
            query: () => `/courses/list`,
            providesTags: ["CourseInventory"],
        }),
        getCourseBuilder: builder.query({
            query: (id) => `/courses/${id}/show`,
            providesTags: ["CourseInventory"],
        }),
        updateCourse: builder.mutation({
            query: (prm) => ({
                url: `/courses/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: [
                "ArticleInventory",
                "CourseInventory",
                "TrackerDep",
            ],
        }),
        deleteTopic: builder.mutation({
            query: (prm) => ({
                url: `/courses/${prm.courseId}/topics/${prm.id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),
        deleteCourse: builder.mutation({
            query: (id) => ({
                url: `/courses/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),

        getCourseLearners: builder.query({
            query: (id) => `/courses/${id}/learners/list`,
            providesTags: ["CourseLearners"],
        }),

        getProgramInventory: builder.query({
            query: () => `/programs/list`,
            providesTags: ["CourseInventory"],
        }),
        getProgramBuilder: builder.query({
            query: (id) => `/programs/${id}/show`,
            providesTags: ["CourseInventory"],
        }),
        updateProgram: builder.mutation({
            query: (prm) => ({
                url: `/programs/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["CourseInventory"],
        }),

        createNewContent: builder.mutation({
            query: (prm) => ({
                url: `/${prm.contentType}/create`,
                method: "POST",
                body: _.omit(prm, "contentType"),
            }),
            invalidatesTags: ["CourseInventory", "ArticleInventory"],
        }),

        getArticleBuilder: builder.query({
            query: (id) => `/articles/${id}/show`,
            providesTags: ["ArticleInventory"],
        }),
        getArticleInventory: builder.query({
            query: () => `/articles/list`,
            providesTags: ["ArticleInventory"],
        }),
        deleteArticle: builder.mutation({
            query: (id) => ({
                url: `/articles/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["ArticleInventory"],
        }),
        updateArticle: builder.mutation({
            query: (prm) => ({
                url: `/articles/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["ArticleInventory", "EngageDep"],
        }),

        createDeck: builder.mutation({
            query: (prm) => ({
                url: `/decks/create`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["CourseInventory", "ArticleInventory"],
        }),
        getDeckBuilder: builder.query({
            query: (id) => `/decks/${id}/showAdmin`,
            providesTags: ["EditingDeck"],
        }),
        updateDeck: builder.mutation({
            query: (prm) => ({
                url: `/decks/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: [
                "EditingDeck",
                "CourseInventory",
                "ArticleInventory",
            ],
        }),
        duplicateDeck: builder.mutation({
            query: (id) => ({
                url: `/decks/${id}/duplicate`,
                method: "POST",
            }),
            invalidatesTags: [
                "EditingDeck",
                "CourseInventory",
                "ArticleInventory",
            ],
        }),
        moveDeck: builder.mutation({
            query: (prm) => ({
                url: `/decks/${prm.id}/move/${prm.direction}`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),
        deleteDeck: builder.mutation({
            query: (id) => ({
                url: `/decks/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["CourseInventory"],
        }),

        getContestInventory: builder.query({
            query: () => `/contests/list`,
            providesTags: ["ContestInventory"],
        }),
        getContestBuilder: builder.query({
            query: (id) => `/contests/${id}/show`,
            providesTags: ["ContestInventory"],
        }),
        updateContest: builder.mutation({
            query: (prm) => ({
                url: `/contests/${prm.id}/update`,
                method: "POST",
                body: prm,
            }),
            invalidatesTags: ["ContestInventory", "TrackerDep"],
        }),
        deleteContest: builder.mutation({
            query: (id) => ({
                url: `/contests/${id}/delete`,
                method: "POST",
            }),
            invalidatesTags: ["ContestInventory"],
        }),

        getContestLearners: builder.query({
            query: (id) => `/contests/${id}/learners/list`,
            providesTags: ["ContestLearners"],
        }),

        // #endregion
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useSendTrackerMutation,
    useGetBannersQuery,
    useGetCourseLibraryQuery,
    useGetArticlesQuery,
    useGetArticleQuery,
    useGetUnauthArticleQuery,
    useGetRecentViewsQuery,
    useGetCourseQuery,
    useGetProgramQuery,
    useGetDeckQuery,
    useGetSessionsQuery,
    useGetAppLeaderboardQuery,
    useGetCourseLeaderboardQuery,
    useGetDeckLeaderboardQuery,
    useGetCollectiblesQuery,
    useGetPostsQuery,
    useGetTagsQuery,
    useSendSearchHistoryMutation,
    useSendArticlesCommentsMutation,
    useSendArticlesLikesMutation,
    useSendArticlesDislikesMutation,
    useGetNotificationsQuery,
    useGetUnreadCountQuery,
    useSendNotificationsReadMutation,
    useSendSocialPostMutation,
    useSendSocialCommentsMutation,
    useSendSocialLikesMutation,
    useSendSocialDeleteMutation,
    useGetLearnerProgressQuery,
    useGetLearnerCourseProgressQuery,
    useGetLearnerPerformanceQuery,
    useGetLearnerCoursePerformanceQuery,
    useGetActivityLogQuery,
    useSendUserSelfUpdateMutation,
    useGetCourseInventoryQuery,
    useGetProgramInventoryQuery,
    useGetProgramBuilderQuery,
    useGetCourseBuilderQuery,
    useGetArticleBuilderQuery,
    useGetArticleInventoryQuery,
    useDeleteArticleMutation,
    useUpdateArticleMutation,
    useCreateNewContentMutation,
    useGetDeckBuilderQuery,
    useUpdateDeckMutation,
    useUpdateCourseMutation,
    useDuplicateDeckMutation,
    useCreateDeckMutation,
    useMoveDeckMutation,
    useDeleteDeckMutation,
    useGetBuilderCourseLeaderboardQuery,
    useDeleteTopicMutation,
    useGetDeckLearnerPathQuery,
    useDeleteCourseMutation,
    useGetCourseLearnersQuery,
    useUpdateProgramMutation,
    useGetCourseLearnersPathQuery,
    useGetDeckPathQuery,
    useGetSurveyStatusPathQuery,
    useGetContestBuilderQuery,
    useGetContestInventoryQuery,
    useDeleteContestMutation,
    useGetContestLearnersQuery,
    useUpdateContestMutation,
    useGetSurveyQuestionsPathQuery,
    useSubmitTicketMutation,
    useGetTicketsQuery,
} = apiV6;
